import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveDynamicComponent as _resolveDynamicComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, Teleport as _Teleport, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, normalizeClass as _normalizeClass, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("strong", { class: "text-xl leading-8 block font-normal text-gray-500 mt-[1.3333333em]" }, "The A or “address” DNS record type was created to hold IPv4 addresses. Translating names to addresses is one of the most fundamental uses of the DNS!", -1)
const _hoisted_2 = /*#__PURE__*/_createElementVNode("a", {
  href: "https://datatracker.ietf.org/doc/html/rfc1034",
  rel: "noopener noreferrer"
}, "RFC 1034", -1)
const _hoisted_3 = /*#__PURE__*/_createElementVNode("a", {
  href: "https://datatracker.ietf.org/doc/html/rfc1035",
  rel: "noopener noreferrer"
}, "1035", -1)
const _hoisted_4 = /*#__PURE__*/_createElementVNode("p", null, "The DNS was created because the original system for mapping names to address, the “hosts” file, was quickly becoming unsustainable. The DNS would allow applications to map human-readable names into addresses in a massive globally distributed database. The A record type associates IPv4 addresses with DNS names.", -1)
const _hoisted_5 = /*#__PURE__*/_createElementVNode("p", null, "Today, the DNS performs many other critical functions. But one of its most important jobs remains the mapping of names to addresses.", -1)
const _hoisted_6 = /*#__PURE__*/_createElementVNode("figure", { class: "mt-6" }, [
  /*#__PURE__*/_createElementVNode("img", {
    class: "w-full rounded-lg",
    src: "/img/learning/dns-record-types/a/the-dns-a-record-type.jpg",
    alt: "Mapping names into addresses",
    title: "Mapping names into addresses",
    width: "1200",
    height: "600"
  }),
  /*#__PURE__*/_createElementVNode("figcaption", null, [
    /*#__PURE__*/_createTextVNode("Mapping names into addresses. By NsLookup.io. Licensed under "),
    /*#__PURE__*/_createElementVNode("a", {
      href: "https://creativecommons.org/licenses/by/4.0/",
      title: "Creative Commons By 4.0 licence",
      rel: "nofollow"
    }, "CC By 4.0"),
    /*#__PURE__*/_createTextVNode(".")
  ])
], -1)
const _hoisted_7 = /*#__PURE__*/_createElementVNode("h2", null, "A record format", -1)
const _hoisted_8 = /*#__PURE__*/_createElementVNode("p", null, "Each A record in the DNS contains a single IPv4 address. Each A record, like all other DNS records, also has a Time-to-Live (TTL) value in seconds.", -1)
const _hoisted_9 = /*#__PURE__*/_createElementVNode("p", null, "When stored in a zone file or displayed as text, use the familar dot-separated notation, e.g. \"1.2.3.4\". When an A record is transported across the Internet in a DNS message, the address is stored in four bytes of binary data to save space.", -1)
const _hoisted_10 = /*#__PURE__*/_createElementVNode("h2", null, "Multiple A records", -1)
const _hoisted_11 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("It is common for a name to map to more than one IPv4 address in the DNS for redundancy. This is accomplished by creating multiple A records at the DNS name. Here is an example showing three address records for "),
  /*#__PURE__*/_createElementVNode("code", null, "www.example.org"),
  /*#__PURE__*/_createTextVNode(" with a TTL value of one hour:")
], -1)
const _hoisted_12 = /*#__PURE__*/_createElementVNode("pre", null, "www.example.org. 3600 A 10.0.0.1\nwww.example.org. 3600 A 10.0.0.2\nwww.example.org. 3600 A 10.0.0.3", -1)
const _hoisted_13 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "The A record type stores IPv4 addresses but it can't hold an IPv6 address. IPv6 wasn't around yet when the DNS specifications were first created. Only a few years after though, IPv6 came along. This required a new DNS record type, and so the AAAA record type was born.")
], -1)
const _hoisted_14 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("The AAAA record type was first introduced in "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc1886",
    rel: "noopener noreferrer"
  }, "RFC 1886"),
  /*#__PURE__*/_createTextVNode(" in 1995. It was later updated by "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc3596",
    rel: "noopener noreferrer"
  }, "RFC 3596"),
  /*#__PURE__*/_createTextVNode(". A and AAAA records both store IP addresses but they are distinct record types from one another.")
], -1)
const _hoisted_15 = /*#__PURE__*/_createElementVNode("p", null, "These two record types are similar except for the size of the address they each carry. 128 bits for type AAAA versus 32 bits for type A.", -1)
const _hoisted_16 = /*#__PURE__*/_createElementVNode("p", null, "It is common to see records of both types at the same name in the DNS. This allows clients to connect using either address type.", -1)
const _hoisted_17 = /*#__PURE__*/_createElementVNode("h2", null, "What TTL should you use for A records?", -1)
const _hoisted_18 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "You should use a TTL of one hour or more for A records that are relatively static. A records that change frequently can have a TTL as low as a few minutes.")
], -1)
const _hoisted_19 = /*#__PURE__*/_createElementVNode("p", null, "Shorter TTLs come with trade-offs. Use them with caution. TTLs under five minutes should be used sparingly. As a general rule of thumb, use shorter TTLs for DNS records that change frequently and longer TTLs for DNS records that are more static.", -1)
const _hoisted_20 = /*#__PURE__*/_createElementVNode("h2", null, "Anycast addresses in the DNS", -1)
const _hoisted_21 = /*#__PURE__*/_createElementVNode("p", null, "In the past it was common to see quite a few A records at a name in the DNS. Often one for each and every server for services with a global footprint.", -1)
const _hoisted_22 = /*#__PURE__*/_createElementVNode("p", null, "Today, the use of Anycast is becoming more and more common. Instead of one address record for each server, a single Anycast address is published.", -1)
const _hoisted_23 = /*#__PURE__*/_createElementVNode("p", null, "When a client attempts to connect to an Anycast address, their request is automatically routed to the nearest datacenter. One or two additional Anycast addresses are often published for redundancy.", -1)
const _hoisted_24 = /*#__PURE__*/_createElementVNode("p", null, "This is very powerful for service providers. It results in smaller DNS messages, faster DNS latency, and less DNS complexity. It also means that their clients always take the shortest path to the provider's servers.", -1)
const _hoisted_25 = /*#__PURE__*/_createElementVNode("p", null, "Anycast addresses are stored in A records the same as Unicast addresses. The DNS does not distinguish between Anycast and Unicast addresses.", -1)
const _hoisted_26 = /*#__PURE__*/_createElementVNode("h2", null, "Load balancing through round robin", -1)
const _hoisted_27 = /*#__PURE__*/_createElementVNode("p", null, "Round robin load balancing in the DNS means responding with different address records each time a name is queried.", -1)
const _hoisted_28 = /*#__PURE__*/_createElementVNode("p", null, "This type of load balancing can be relatively effective. However, due to DNS caching the results are generally suboptimal at best.", -1)
const _hoisted_29 = /*#__PURE__*/_createElementVNode("p", null, "Load balancing through Anycast in combination with software or hardware load balancer devices gives better results. However, Anycast is more complex to set up and maintain.", -1)
const _hoisted_30 = /*#__PURE__*/_createElementVNode("h2", null, "Other uses of A records in the DNS", -1)
const _hoisted_31 = /*#__PURE__*/_createElementVNode("p", null, "A common theme in Internet operations is to use the DNS in different and sometimes exciting ways! Even the A record serves other purposes. Its uses have included:", -1)
const _hoisted_32 = /*#__PURE__*/_createElementVNode("ul", null, [
  /*#__PURE__*/_createElementVNode("li", null, "Domain Name System-based Blocklist (DNSBL)"),
  /*#__PURE__*/_createElementVNode("li", null, "Storing subnets in the DNS")
], -1)
const _hoisted_33 = /*#__PURE__*/_createElementVNode("h3", null, "Domain Name System-based Blocklist (DNSBL)", -1)
const _hoisted_34 = /*#__PURE__*/_createElementVNode("p", null, "DNSBLs use the DNS as a distributed database to store blocklists used to defeat email spam.", -1)
const _hoisted_35 = /*#__PURE__*/_createElementVNode("p", null, "A mail server can perform a DNS query with a specific format against a DNSBL to check if a sending host's IP address has been flagged as a source of spam. In this case the mail server might reject or throttle requests from that sender's IP address.", -1)
const _hoisted_36 = /*#__PURE__*/_createElementVNode("p", null, "A mail server would follow these steps to use a DNSBL:", -1)
const _hoisted_37 = /*#__PURE__*/_createElementVNode("ol", null, [
  /*#__PURE__*/_createElementVNode("li", null, [
    /*#__PURE__*/_createTextVNode("Construct a DNSBL lookup name by reversing the octets in the sender's IP address and appending the DNSBL's domain name. For example if the sender's IP address is 1.2.3.4 the lookup name for the example.org DNSBL would be "),
    /*#__PURE__*/_createElementVNode("code", null, "4.3.2.1.dnsbl.example.org.")
  ]),
  /*#__PURE__*/_createElementVNode("li", null, "Perform a standard DNS lookup with query type A for the lookup name."),
  /*#__PURE__*/_createElementVNode("li", null, "If the result is NXDOMAIN there is no entry for the sender's IP in the blocklist. This means that the sender's IP is not flagged as a source of spam. Mail should be sent normally."),
  /*#__PURE__*/_createElementVNode("li", null, "A positive result indicates that the sender's IP exists in the blocklist. This means the IP is considered a source of spam. Optionally, a TXT lookup for the same name can be performed to retrieve the reason for blocklisting.")
], -1)
const _hoisted_38 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("The address returned for a blocked IP address may be the fixed value 127.0.0.2. This indicates that the sender's IP address has been flagged as a source of spam. Other values are also possible. See "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc5782#section-2.3",
    rel: "noopener noreferrer"
  }, "RFC 5782 section 2.3"),
  /*#__PURE__*/_createTextVNode(" for more details.")
], -1)
const _hoisted_39 = /*#__PURE__*/_createElementVNode("h3", null, "Storing subnets in the DNS", -1)
const _hoisted_40 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc1101#section-4",
    rel: "noopener noreferrer"
  }, "RFC 1101 section 4"),
  /*#__PURE__*/_createTextVNode(" proposes a method to store the subnet mask for a network in the DNS. A DNS type A record is created at the network's reverse lookup name in the in-addr.arpa zone.")
], -1)
const _hoisted_41 = /*#__PURE__*/_createElementVNode("p", null, "This is not used today as there is little need to publish subnet masks in the DNS. But it is interesting to see how the use of the DNS has evolved over the years.", -1)
const _hoisted_42 = /*#__PURE__*/_createElementVNode("h2", null, "Looking up A records for a name", -1)
const _hoisted_43 = /*#__PURE__*/_createElementVNode("p", null, "The IPv4 address records for a DNS name can be queried with the dig or nslookup command line tools. To find the address records for wikipedia.org, use this command:", -1)
const _hoisted_44 = /*#__PURE__*/_createElementVNode("pre", null, "dig A wikipedia.org", -1)
const _hoisted_45 = /*#__PURE__*/_createElementVNode("p", null, "On operating systems that support nslookup, you can use the following:", -1)
const _hoisted_46 = /*#__PURE__*/_createElementVNode("pre", null, "nslookup -type=a wikipedia.org", -1)
const _hoisted_47 = {
  class: "not-prose text-base mx-auto max-w-lg",
  autocomplete: "off"
}
const _hoisted_48 = { class: "mx-auto max-w-3xl divide-y-2 divide-gray-200" }
const _hoisted_49 = /*#__PURE__*/_createElementVNode("h2", { class: "ignore-toc" }, "Related questions", -1)
const _hoisted_50 = { class: "mt-6 space-y-6 divide-y divide-gray-200 [overflow-anchor:none]" }
const _hoisted_51 = /*#__PURE__*/_createElementVNode("span", { class: "text-lg font-medium text-gray-900" }, " What is the difference between a CNAME record and an A record? ", -1)
const _hoisted_52 = { class: "ml-6 flex h-7 items-center" }
const _hoisted_53 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", { class: "font-medium text-gray-500" }, "A CNAME is an alias from one DNS name to another. A CNAME is often used to redirect one DNS name to another DNS name which contains a set of A records.")
], -1)
const _hoisted_54 = /*#__PURE__*/_createElementVNode("p", null, "One use of CNAMEs is to eliminate duplicate data from the DNS. This reduces the chance of an administrator forgetting to update all copies of duplicated A records when making a change. CNAMEs may also be linked together in chains. The last name in the CNAME chain should usually resolve to a set of A records.", -1)
const _hoisted_55 = /*#__PURE__*/_createElementVNode("span", { class: "text-lg font-medium text-gray-900" }, " Does a domain need an A record? ", -1)
const _hoisted_56 = { class: "ml-6 flex h-7 items-center" }
const _hoisted_57 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", { class: "font-medium text-gray-500" }, [
    /*#__PURE__*/_createTextVNode("A domain such as "),
    /*#__PURE__*/_createElementVNode("code", null, "example.org"),
    /*#__PURE__*/_createTextVNode(" does not necessarily need an A record. However most domains do have an A record. It is typical for web clients to interchangeably connect to both "),
    /*#__PURE__*/_createElementVNode("code", null, "example.org"),
    /*#__PURE__*/_createTextVNode(" and "),
    /*#__PURE__*/_createElementVNode("code", null, "www.example.org"),
    /*#__PURE__*/_createTextVNode(".")
  ])
], -1)
const _hoisted_58 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("If there are no A records for "),
  /*#__PURE__*/_createElementVNode("code", null, "example.org"),
  /*#__PURE__*/_createTextVNode(" then web browsers would display an error if a user did not explicitly type "),
  /*#__PURE__*/_createElementVNode("code", null, "www.example.org"),
  /*#__PURE__*/_createTextVNode(". This would be confusing for clients of the domain.")
], -1)

import BlogPost from '@/components/layout/BlogPost.vue';


import { useRouter } from 'vue-router';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/vue';
import { ChevronDownIcon } from '@heroicons/vue/24/outline';
import { jeff, ruurtjan } from '@/common/authors';
import { BlogMeta } from '@/common/blogMeta';
import DomainSearch from '@/components/DomainSearch.vue';
import CodeBlock from '@/components/micro/CodeBlock.vue';
import DnsRecordTypes from '@/views/learning/DnsRecordTypes.vue';
import { learningBreadcrumb } from '@/model/Breadcrumb';
import Callout from '@/components/layout/CalloutComponent.vue';
import { domainNameToUrlPath } from '@/common/domainName';

const meta: BlogMeta = {
  tag: 'DNS record types',
  url: '/learning/dns-record-types/a/',
  breadcrumbs: [learningBreadcrumb, DnsRecordTypes.breadcrumb],
  title: 'The A record',
  description: 'The A or “address” DNS record type maps DNS names to IPv4 addresses. Translating names to addresses is one of the most fundamental uses of the DNS.',
  overviewDescription: 'The A or “address” DNS record type maps DNS names to IPv4 addresses. Translating names to addresses is one of the most fundamental uses of the DNS.',
  overviewImage: '/img/learning/dns-record-types/a/the-dns-a-record-type-small.jpg',
  overviewImageAlt: 'Mapping names into numbers',
  author: jeff,
  editor: ruurtjan,
  img1024x512: 'https://www.nslookup.io/img/learning/dns-record-types/a/the-a-record-social.jpg',
  img1200x630: 'https://www.nslookup.io/img/learning/dns-record-types/a/the-a-record-social.jpg',
  written: new Date('2022-07-11T13:19:14.101Z'),
  updated: new Date('2023-03-28T11:56:49.659Z'),
};

const __default__ = {
  meta,
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'RecordTypeA',
  setup(__props) {

const router = useRouter();

function redirectTo(domain: string): void {
  router.push(`/domains/${domainNameToUrlPath(domain)}/dns-records/a/`);
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_openBlock(), _createBlock(_Teleport, { to: "head" }, [
      (_openBlock(), _createBlock(_resolveDynamicComponent('script'), { type: "application/ld+json" }, {
        default: _withCtx(() => [
          _createTextVNode(" { \"@context\": \"https://schema.org\", \"@type\": \"ImageObject\", \"author\": { \"@type\": \"Person\", \"email\": \"mailto:ruurtjan@nslookup.io\", \"image\": \"https://www.nslookup.io/img/ruurtjan-lg.jpg\", \"jobTitle\": \"CEO\", \"name\": \"Ruurtjan Pul\", \"url\": \"https://www.nslookup.io/about/ruurtjan-pul/\" }, \"copyrightHolder\": { \"@type\": \"Organization\", \"url\": \"https://www.nslookup.io/\", \"email\": \"hello@nslookup.io\" }, \"copyrightYear\": 2023, \"contentUrl\": \"https://www.nslookup.io/img/learning/dns-record-types/a/the-dns-a-record-type.jpg\", \"license\": \"https://creativecommons.org/licenses/by/4.0/\", \"acquireLicensePage\": \"https://creativecommons.org/licenses/by/4.0/\" } ")
        ]),
        _: 1
      }))
    ])),
    _createVNode(BlogPost, {
      title: meta.title,
      description: meta.description,
      url: `https://www.nslookup.io${meta.url}`,
      breadcrumbs: meta.breadcrumbs,
      img1024x512: meta.img1024x512,
      img1200x630: meta.img1200x630,
      written: meta.written,
      updated: meta.updated,
      author: meta.author,
      editor: meta.editor
    }, {
      default: _withCtx(() => [
        _hoisted_1,
        _createElementVNode("p", null, [
          _createTextVNode("The A record type was introduced in the original DNS specifications ("),
          _hoisted_2,
          _createTextVNode(" and "),
          _hoisted_3,
          _createTextVNode(") in 1987. Each type A record holds an IPv4 address. IPv6 did not yet exist in 1987 so all addresses were 32-bit IPv4 addresses. The "),
          _createVNode(_component_router_link, { to: "/learning/dns-record-types/aaaa/" }, {
            default: _withCtx(() => [
              _createTextVNode("AAAA DNS record type")
            ]),
            _: 1
          }),
          _createTextVNode(" for IPv6 records would come later.")
        ]),
        _hoisted_4,
        _hoisted_5,
        _hoisted_6,
        _hoisted_7,
        _hoisted_8,
        _hoisted_9,
        _hoisted_10,
        _hoisted_11,
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_12
          ]),
          _: 1
        }),
        _createVNode(Callout, {
          icon: "info",
          title: "A records versus AAAA records",
          heading: "h2"
        }, {
          default: _withCtx(() => [
            _hoisted_13,
            _hoisted_14,
            _hoisted_15,
            _hoisted_16
          ]),
          _: 1
        }),
        _hoisted_17,
        _hoisted_18,
        _createElementVNode("p", null, [
          _createTextVNode("TTL selection in the DNS is often complicated. As we discussed in "),
          _createVNode(_component_router_link, { to: "/learning/what-is-a-good-ttl-for-dns/" }, {
            default: _withCtx(() => [
              _createTextVNode("our article on TTL selection in the DNS")
            ]),
            _: 1
          }),
          _createTextVNode(", shorter TTLs give agility but longer TTLs increase reliability. Longer TTLs can also decrease latency.")
        ]),
        _hoisted_19,
        _hoisted_20,
        _hoisted_21,
        _hoisted_22,
        _hoisted_23,
        _hoisted_24,
        _hoisted_25,
        _hoisted_26,
        _hoisted_27,
        _createElementVNode("p", null, [
          _createTextVNode("Each time the DNS servers for a "),
          _createVNode(_component_router_link, {
            to: "/learning/what-is-a-dns-zone/",
            title: "DNS zone"
          }, {
            default: _withCtx(() => [
              _createTextVNode("DNS zone")
            ]),
            _: 1
          }),
          _createTextVNode(" receive a query, they respond with a different set of address records. The idea is that different clients will use different endpoint addresses. Ideally, all possible addresses will be in use by different clients at the same time. This distributes load across all available servers.")
        ]),
        _hoisted_28,
        _hoisted_29,
        _hoisted_30,
        _hoisted_31,
        _hoisted_32,
        _hoisted_33,
        _hoisted_34,
        _hoisted_35,
        _hoisted_36,
        _hoisted_37,
        _hoisted_38,
        _hoisted_39,
        _hoisted_40,
        _hoisted_41,
        _hoisted_42,
        _hoisted_43,
        _hoisted_44,
        _hoisted_45,
        _hoisted_46,
        _createElementVNode("p", null, [
          _createTextVNode("You can also check the A records for any domain name in our "),
          _createVNode(_component_router_link, { to: "/a-lookup/" }, {
            default: _withCtx(() => [
              _createTextVNode("A lookup")
            ]),
            _: 1
          }),
          _createTextVNode(" tool, or by entering it here:")
        ]),
        _createElementVNode("form", _hoisted_47, [
          _createVNode(DomainSearch, {
            emphasize: "",
            buttonText: "Find A record",
            onSubmitted: redirectTo
          })
        ]),
        _createElementVNode("div", _hoisted_48, [
          _hoisted_49,
          _createElementVNode("div", _hoisted_50, [
            _createVNode(_unref(Disclosure), {
              as: "div",
              class: "pt-6"
            }, {
              default: _withCtx(({ open }) => [
                _createVNode(_unref(DisclosureButton), {
                  as: "h3",
                  class: "ignore-toc flex mt-0 w-full items-start justify-between text-left cursor-pointer text-gray-400"
                }, {
                  default: _withCtx(() => [
                    _hoisted_51,
                    _createElementVNode("span", _hoisted_52, [
                      _createVNode(_unref(ChevronDownIcon), {
                        class: _normalizeClass([open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform']),
                        "aria-hidden": "true"
                      }, null, 8, ["class"])
                    ])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_unref(DisclosurePanel), { class: "mt-2 pr-12" }, {
                  default: _withCtx(() => [
                    _hoisted_53,
                    _hoisted_54,
                    _createElementVNode("p", null, [
                      _createTextVNode("See our article on "),
                      _createVNode(_component_router_link, { to: "/learning/dns-record-types/cname/" }, {
                        default: _withCtx(() => [
                          _createTextVNode("CNAME records")
                        ]),
                        _: 1
                      }),
                      _createTextVNode(" for more details.")
                    ])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_unref(Disclosure), {
              as: "div",
              class: "pt-6"
            }, {
              default: _withCtx(({ open }) => [
                _createVNode(_unref(DisclosureButton), {
                  as: "h3",
                  class: "ignore-toc flex mt-0 w-full items-start justify-between text-left cursor-pointer text-gray-400"
                }, {
                  default: _withCtx(() => [
                    _hoisted_55,
                    _createElementVNode("span", _hoisted_56, [
                      _createVNode(_unref(ChevronDownIcon), {
                        class: _normalizeClass([open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform']),
                        "aria-hidden": "true"
                      }, null, 8, ["class"])
                    ])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_unref(DisclosurePanel), { class: "mt-2 pr-12" }, {
                  default: _withCtx(() => [
                    _hoisted_57,
                    _hoisted_58
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ])
        ])
      ]),
      _: 1
    }, 8, ["title", "description", "url", "breadcrumbs", "img1024x512", "img1200x630", "written", "updated", "author", "editor"])
  ], 64))
}
}

})